import {
  cacheKeys,
  useAllProducts,
  useCurrentLocation,
  useCurrentWorkspace,
  useCustomMutation,
  useProduct,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useLocalSearchParams } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { RadioButton, Text } from 'react-native-paper';
import { string, object as yupObject } from 'yup';
import { formStyles } from '../../styles/forms';
import { Colors } from '../../themes/colors';
import { BarcodeScanInput } from '../crop-circle/Components/BarcodeScanInput';
import { tokens } from '../crop-circle/tokens';
import { ControlledCheckboxItems, ControlledTextInput, Select } from '../forms';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';

export type NewOemAssetStyles = {
  container: ViewStyle;
  contentContainer: ViewStyle;
  imageGalleryContainer?: ViewStyle;
};

export type NewOemAssetProps = {
  onCancel: () => void;
  topComponentBar: any;
  styles: StyleSheet.NamedStyles<NewOemAssetStyles>;
  onSaved: (assetId: string) => void;
};

export const NewOemAsset = (props: NewOemAssetProps) => {
  return <NewOemAssetBase {...props} />;
};

export const NewOemAssetBase = (props: NewOemAssetProps) => {
  const params = useLocalSearchParams();
  const { tagId, tagCode } = params;
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace();
  const { getLocation } = useCurrentLocation();
  const products = useAllProducts(workspace?.id);
  const formSchema = yupObject({
    product: string().required(),
    name: string().required(),
    grippId: string().required(),
  });
  const queryClient = useQueryClient();
  const { mutate } = useCustomMutation({
    query: createAsset,
  });
  // Double-submit guards — see onSubmit. The ref blocks re-entry synchronously
  // (before React re-renders the disabled button); the state drives the
  // disabled styling.
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  type NewOemAssetFormValues = {
    product: string;
    name: string;
    serialNumber?: string;
    addons?: string[];
    grippId: string;
  };

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { isValid, isDirty },
  } = useForm<NewOemAssetFormValues>({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      product: '',
      name: '',
      serialNumber: '',
      addons: [],
      grippId: tagCode ? String(tagCode) : '',
    },
  });

  const selectedProductId = watch('product');
  const { product: selectedProduct } = useProduct(selectedProductId || '');
  const previousProductIdRef = useRef<string>('');

  // Clear selected addons when product changes
  useEffect(() => {
    if (
      previousProductIdRef.current &&
      previousProductIdRef.current !== selectedProductId
    ) {
      setValue('addons', [], { shouldDirty: true });
    }
    previousProductIdRef.current = selectedProductId || '';
  }, [selectedProductId, setValue]);

  const canSave = useMemo(() => {
    return isDirty && isValid && !isSubmitting;
  }, [isValid, isDirty, isSubmitting]);

  const productItems = useMemo(() => {
    return (
      products?.products?.map((p: any) => ({
        label: p.name,
        value: p.id,
      })) || []
    );
  }, [products?.products]);

  const tickIconComponent = useMemo(
    () => () => <RadioButton value="" status="checked" />,
    []
  );

  const dropDownPickerProps = useMemo(
    () => ({
      listMode: 'SCROLLVIEW' as const,
      showTickIcon: true,
      selectedItemLabelStyle: { color: Colors.role.textPrimary },
      selectedItemContainerStyle: {
        backgroundColor: Colors.role.surface,
      },
      TickIconComponent: tickIconComponent,
      dropDownDirection: 'AUTO' as const,
    }),
    [tickIconComponent]
  );

  const onSubmit = async (data: NewOemAssetFormValues) => {
    // Prevent a second addAsset from firing for the same tag. The Save button
    // is not disabled until React re-renders, and the awaited getLocation()
    // below can take seconds on mobile (GPS fix), so an impatient second tap
    // would create a duplicate asset — the server then re-points the tag to the
    // newest one and orphans the first as a "tagless" asset.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      let location;
      if (tagId) {
        location = await getLocation();
      }
      // grippId is a form-only field (the tag code shown to the user) and is not
      // part of AddAssetInput; drop it before building the mutation input.
      // Destructured out rather than `delete`d — grippId is a required field, so
      // `delete` would fail strict TS (TS2790).
      const { grippId: _grippId, ...assetInput } = data;
      const input: any = {
        ...assetInput,
        ...(tagId && { tagId }),
        ...(location && { lastLocation: location }),
        workspaceId: workspace.id,
      };

      mutate(
        { input: input },
        {
          onSettled: (response: any) => {
            queryClient.invalidateQueries({ queryKey: cacheKeys('asset').all });
            const assetId = response?.asset?.addAsset?.id;
            if (assetId) {
              props.onSaved(assetId);
            } else {
              // Creation failed — re-enable so the user can retry.
              submittingRef.current = false;
              setIsSubmitting(false);
            }
          },
        }
      );
    } catch {
      // getLocation() rejected (e.g. permission/timeout) — re-enable to retry.
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const formContent = (
    <ScrollView
      showsHorizontalScrollIndicator={false}
      scrollEnabled
      showsVerticalScrollIndicator={false}
      style={props.styles.contentContainer}
      automaticallyAdjustKeyboardInsets={true}
      keyboardDismissMode="interactive"
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.outerContainer}>
        <View style={[styles.baseContainer, styles.productContainer]}>
          <View>
            <Text style={formStyles.formItemTitle}>
              {t('asset.productType')}
            </Text>
          </View>
          <Select
            label=""
            listMode={Platform.OS === 'web' ? 'SCROLLVIEW' : 'MODAL'}
            items={productItems}
            onSelectItem={(item: any) => {
              setValue('product', item);
            }}
            dropDownPickerProps={dropDownPickerProps}
            dropDownContainerStyle={
              Platform.OS === 'android'
                ? {
                    position: 'relative',
                    top: 0,
                  }
                : undefined
            }
          />
        </View>
        <View style={[styles.baseContainer, styles.formItem]}>
          <View>
            <Text style={formStyles.formItemTitle}>{t('asset.name')}</Text>
          </View>
          <ControlledTextInput
            testID="add-asset-name"
            control={control as any}
            name="name"
            placeholder={t('asset.namePlaceholder')}
            backgroundColor={Colors.role.inputBackground}
            outlinedTextInput
            disableInnerStyle
            hideBorder
          />
        </View>
        <View style={[styles.baseContainer, styles.formItem]}>
          <View>
            <Text style={formStyles.formItemTitle}>
              {t('asset.serialNumber')}
            </Text>
          </View>
          <Controller
            control={control as any}
            name="serialNumber"
            render={({ field: { onChange, value } }) => (
              <BarcodeScanInput
                testID="add-asset-serial-number"
                value={value ?? ''}
                onChangeText={onChange}
                onScanned={(scanned) => {
                  setValue('serialNumber', scanned, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                hideBorder
                backgroundColor={Colors.role.inputBackground}
                placeholder={t('common.optional')}
              />
            )}
          />
        </View>
        <View style={[styles.baseContainer, styles.formItem]}>
          <View>
            <Text style={formStyles.formItemTitle}>
              {t('asset.addons', {
                count: selectedProduct?.addons?.length || 0,
              })}
            </Text>
          </View>
          <ControlledCheckboxItems
            control={control as any}
            name="addons"
            options={
              selectedProduct?.addons
                ?.slice()
                .sort((a: any, b: any) => a.name.localeCompare(b.name))
                .map((p: any) => ({
                  label: p.name,
                  value: p.id,
                })) || []
            }
            position="leading"
            mode="android"
            labelStyle={{ textAlign: 'left' }}
          />
        </View>
        {tagCode && (
          <View
            style={[
              styles.baseContainer,
              styles.formItem,
              styles.formItemBottom,
            ]}
          >
            <View>
              <Text style={formStyles.formItemTitle}>{t('asset.tagCode')}</Text>
            </View>
            <ControlledTextInput
              testID="add-asset-gripp-id"
              control={control as any}
              name="grippId"
              outlinedTextInput
              editable={false}
              disableInnerStyle={true}
              textColor={Colors.role.textSecondary}
              backgroundColor={Colors.role.inputBackground}
            />
          </View>
        )}
      </View>
    </ScrollView>
  );

  return (
    <>
      <KeyboardSafeView style={[props.styles.container]}>
        {formContent}
      </KeyboardSafeView>
      <View style={styles.footer}>
        <TouchableOpacity
          style={[
            styles.footerSaveButton,
            !canSave && styles.footerSaveButtonDisabled,
          ]}
          onPress={canSave ? handleSubmit(onSubmit) : undefined}
          disabled={!canSave}
        >
          <Text variant="bodyLarge" style={styles.footerSaveButtonText}>
            {t('asset.save')}
          </Text>
        </TouchableOpacity>
      </View>
    </>
  );
};

const createAsset = `
  mutation Asset($input: AddAssetInput!) {
    asset {
      addAsset(input: $input) {
        id
      }
    }
  }
`;

const styles = StyleSheet.create({
  outerContainer: {
    paddingBottom: 200,
    backgroundColor: Colors.role.canvas,
  },
  baseContainer: {
    paddingVertical: tokens.spacing.md,
    flexDirection: 'column',
    width: '100%',
  },
  productContainer: {
    zIndex: 1000,
  },
  formItem: {
    width: '100%',
  },
  formItemBottom: {
    marginBottom: 40,
  },
  footer: {
    position: 'absolute',
    left: tokens.spacing.xl,
    right: tokens.spacing.xl,
    bottom: 0,
    paddingVertical: tokens.spacing.xl,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  footerSaveButton: {
    backgroundColor: Colors.role.surfaceInverse,
    borderRadius: tokens.borderRadius.sm,
    width: '100%',
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerSaveButtonDisabled: {
    backgroundColor: Colors.role.borderStrong,
  },
  footerSaveButtonText: {
    // White on a strong gray fill: constant in both modes. `textOnInverse`
    // would go dark here and vanish against the dark-mode fill.
    color: tokens.colors.brand.white,
    fontWeight: tokens.typography.body.mediumEmphasised.fontWeight,
  },
});
